import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { getBusinessConfig } from "@/lib/businessConfig";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { useMemo } from "react";

const SOCIAL_ICONS = [
  { key: "facebook" as const, Icon: Facebook },
  { key: "instagram" as const, Icon: Instagram },
  { key: "linkedin" as const, Icon: Linkedin },
  { key: "youtube" as const, Icon: Youtube },
];

export default function Footer() {
  const { themeState } = useTheme();
  const { settings } = useSiteSettings();
  const businessType = themeState.published.businessType;
  const f = settings.footer;
  const c = settings.contact;

  const navLinks = useMemo(() => {
    const config = getBusinessConfig(businessType);
    return config.pages.map((p) => ({
      name: p.label,
      path: p.id === "home" ? "/" : `/${p.id}`,
    }));
  }, [businessType]);

  const phoneHref = `tel:${c.phone.replace(/\s/g, "")}`;

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container-luxury px-4 sm:px-6 py-12 md:py-16 lg:py-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4 md:mb-6">
              {f.showLogoInsteadOfText && f.footerLogo ? (
                <img src={f.footerLogo} alt={`${f.brandName1} ${f.brandName2}`} className="max-h-10 object-contain" width={160} height={40} loading="lazy" />
              ) : (
                <>
                  <span className="font-serif text-xl md:text-2xl font-bold">{f.brandName1}</span>
                  <span className="gold-gradient-text font-serif text-xl md:text-2xl font-bold">
                    {f.brandName2}
                  </span>
                </>
              )}
            </Link>
            <p className="text-primary-foreground/70 text-xs md:text-sm leading-relaxed mb-4 md:mb-6">
              {f.description}
            </p>
            <div className="flex gap-3 md:gap-4">
              {SOCIAL_ICONS.map(({ key, Icon }) => (
                <a
                  key={key}
                  href={f.socialLinks[key] || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${key}`}
                  className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center hover:bg-accent hover:border-accent transition-all duration-300 group"
                >
                  <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:text-accent-foreground" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-base md:text-lg font-semibold mb-4 md:mb-6">Quick Links</h3>
            <nav className="flex flex-col gap-2 md:gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-primary-foreground/70 text-xs md:text-sm hover:text-accent transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/privacy" className="text-primary-foreground/70 text-xs md:text-sm hover:text-accent transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-primary-foreground/70 text-xs md:text-sm hover:text-accent transition-colors">
                Terms & Conditions
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div className="sm:col-span-2 lg:col-span-2">
            <h3 className="font-serif text-base md:text-lg font-semibold mb-4 md:mb-6">Contact Us</h3>
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 md:w-5 md:h-5 text-accent mt-0.5 flex-shrink-0" />
                <span className="text-primary-foreground/70 text-xs md:text-sm whitespace-pre-line">
                  {c.address}
                </span>
              </div>
              <a href={phoneHref} className="flex items-center gap-3 text-primary-foreground/70 text-xs md:text-sm hover:text-accent transition-colors">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
                {c.phone}
              </a>
              <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-primary-foreground/70 text-xs md:text-sm hover:text-accent transition-colors">
                <Mail className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
                {c.email}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 md:mt-16 pt-6 md:pt-8 border-t border-primary-foreground/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
            <p className="text-primary-foreground/50 text-xs md:text-sm text-center md:text-left">
              © {new Date().getFullYear()} {f.copyrightText}
            </p>
            <p className="text-primary-foreground/50 text-[10px] md:text-xs text-center">
              {f.disclaimerText}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
